import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  login as loginAction,
  logout as logoutAction,
  setUser,
  register as registerAction,
  getProfileAction,
} from "../store/slices/authSlice";
import { toast } from "react-toastify";

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    loading: authLoading,
  } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Initialize auth state only once
  useEffect(() => {
    const checkAuth = async () => {
      if (initialized) return;

      try {
        const result = await dispatch(getProfileAction()).unwrap();
        if (result?.user) {
          dispatch(setUser(result.user));
        }
        if (result?.token) {
          localStorage.setItem("token", result.token);
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        // Don't redirect on initial check
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    checkAuth();
  }, [dispatch, initialized]);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      const result = await dispatch(loginAction(credentials)).unwrap();
      if (result?.user) {
        dispatch(setUser(result.user));
        if (result.token) {
          localStorage.setItem("token", result.token);
        }
        toast.success("Login successful!");

        // Redirect based on role
        switch (result.user.role) {
          case "admin":
            navigate("/admin/admin-dashboard");
            break;
          case "superadmin":
            navigate("/admin/dashboard");
            break;
          default:
            navigate("/user/dashboard");
        }
        return result; // Return result for caller
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError("");
      const result = await dispatch(registerAction(userData)).unwrap();
      if (result?.user) {
        dispatch(setUser(result.user));
        return result;
      }
    } catch (error) {
      setError(error.message || "Registration failed");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      await dispatch(logoutAction()).unwrap();
      localStorage.removeItem("token");
      navigate("/login");
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to logout");
    } finally {
      setLoading(false);
    }
  };

  // Check if user has required role
  const hasRole = (roles) => {
    if (!user) return false;
    if (isSuperAdmin()) return true; // Super admin can access everything
    return roles.includes(user.role);
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return user?.role === "superadmin";
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === "admin";
  };

  // Context value
  const value = {
    user,
    isAuthenticated,
    loading: loading || authLoading,
    error,
    isSuperAdmin,
    isAdmin,
    hasRole,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
